import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getEntries } from "@/lib/time-api";
import { useOrgId } from "@/lib/org-context";
import type { TimeEntry, Pagination } from "@/types/time";

interface UseTimeEntriesOptions {
  startDate?: string;
  endDate?: string;
  projectId?: string;
  memberId?: string;
  memberIds?: string;
  teamId?: string;
  perPage?: number;
  page?: number;
  enabled?: boolean;
}

export function useTimeEntries(options: UseTimeEntriesOptions = {}) {
  const queryClient = useQueryClient();
  const orgId = useOrgId();
  const {
    startDate,
    endDate,
    projectId,
    memberId,
    memberIds,
    teamId,
    perPage = 50,
    page = 1,
    enabled = true,
  } = options;

  const queryKey = [
    "time-entries",
    orgId,
    { startDate, endDate, projectId, memberId, memberIds, teamId, page, perPage },
  ];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      getEntries({
        start_date: startDate,
        end_date: endDate,
        project_id: projectId,
        member_id: memberId,
        member_ids: memberIds,
        team_id: teamId,
        page,
        per_page: perPage,
      }),
    enabled: enabled && !!orgId,
  });

  function refetch() {
    queryClient.invalidateQueries({ queryKey: ["time-entries"] });
  }

  return {
    entries: (data?.data ?? []) as TimeEntry[],
    pagination: (data?.pagination ?? null) as Pagination | null,
    loading: isLoading,
    refetch,
  };
}
